import React, { ReactNode, useCallback, useMemo, useState } from 'react';

import type { TelegramAuthResult } from '@/shared/api/channels/telegramPersonal';

import { createStrictContext, useStrictContext } from '@/shared/lib/react';

import type { TStep } from '../../model/telegramPersonal/types';

interface ITelegramAuthContext {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  authResult?: TelegramAuthResult;
  applyAuthResult: (result: TelegramAuthResult) => void;
  step: TStep;
  setStep: (step: TStep) => void;
}

interface ITelegramAuthProviderProps {
  children: ReactNode;
  initialStep?: TStep;
}

const TelegramAuthContext = createStrictContext<ITelegramAuthContext>();

export const TelegramAuthProvider = ({
  children,
  initialStep = 'selectMethod',
}: ITelegramAuthProviderProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<TStep>(initialStep);
  const [authResult, setAuthResult] = useState<TelegramAuthResult>();
  const applyAuthResult = useCallback((result: TelegramAuthResult) => {
    setAuthResult(result);
    setStep(result.step === 'code' || result.step === 'email_setup_code' ? 'sms' : result.step);
  }, []);

  const value: ITelegramAuthContext = useMemo(
    () => ({
      authResult,
      applyAuthResult,
      isLoading,
      setIsLoading,
      step,
      setStep,
    }),
    [authResult, applyAuthResult, isLoading, setIsLoading, step, setStep]
  );

  return (
    <TelegramAuthContext.Provider value={value}>
      {children}
    </TelegramAuthContext.Provider>
  );
};

export const useTelegramAuth = (): ITelegramAuthContext => {
  return useStrictContext(TelegramAuthContext);
};
